import React, { useState } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import { showToast } from '../../utils/toast';
import { userService } from '../../services/userService';

const ChangePasswordTab = () => {
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [captcha, setCaptcha] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.newPassword !== form.confirmPassword) {
      showToast.error('Passwords do not match');
      return;
    }

    if (form.newPassword.length < 6) {
      showToast.error('Password must be at least 6 characters long');
      return;
    }

    if (!captcha) {
      showToast.error('Please verify that you are not a robot');
      return;
    }

    try {
      setLoading(true);
      
      // Call the real API
      await userService.changePassword(form.newPassword, form.confirmPassword);
      
      showToast.success('Password updated successfully!', {
        position: 'top-center',
        autoClose: 3000,
      });
      
      // Reset form
      setForm({ newPassword: '', confirmPassword: '' });
      setCaptcha(false);
    } catch (error) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.errors?.[0]?.msg || 
                          'Failed to update password. Please try again.';
      showToast.error(errorMessage, {
        position: 'top-center',
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-tab p-3">
      <div className="d-flex justify-content-center">
        <Card style={{ width: '100%'}} className="shadow-sm">
          <Card.Header className="bg-primary text-white text-center py-3">
            <h4 className="mb-0 fw-bold">Change Password</h4>
          </Card.Header>
          <Card.Body className="p-5">
            <Form onSubmit={handleSubmit}>
              
              {/* Password Section */}
              <div className="mb-4">
                <h6 className="text-primary fw-bold mb-3 border-bottom pb-2">Password Information</h6>
                <div className="row g-4">
                  <div className="col-md-6">
                    <Form.Group controlId="newPassword" className="mb-3">
                      <Form.Label className="fw-semibold mb-2">New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="newPassword"
                        placeholder="Enter new password"
                        value={form.newPassword}
                        onChange={handleChange}
                        required
                        className="border-0 bg-light py-2"
                        style={{ fontSize: '14px' }}
                      />
                      <Form.Text className="text-muted mt-1">
                        <small>Password must be at least 6 characters long</small>
                      </Form.Text>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group controlId="confirmPassword" className="mb-3">
                      <Form.Label className="fw-semibold mb-2">Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm new password"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        required
                        className="border-0 bg-light py-2"
                        style={{ fontSize: '14px' }}
                      />
                      {form.confirmPassword && form.newPassword !== form.confirmPassword && (
                        <Form.Text className="text-danger mt-1">
                          <small><i className="fas fa-exclamation-triangle me-1"></i>Passwords do not match</small>
                        </Form.Text>
                      )}
                      {form.confirmPassword && form.newPassword === form.confirmPassword && form.confirmPassword.length >= 6 && (
                        <Form.Text className="text-success mt-1">
                          <small><i className="fas fa-check-circle me-1"></i>Passwords match</small>
                        </Form.Text>
                      )}
                    </Form.Group>
                  </div>
                </div>
              </div>

              {/* Security Section */}
              <div className="mb-4">
                <h6 className="text-primary fw-bold mb-3 border-bottom pb-2">Security Verification</h6>
                <div className="d-flex align-items-center justify-content-center py-3">
                  <div className="border rounded p-3 bg-light me-3 shadow-sm">
                    <Form.Check
                      type="checkbox"
                      id="captchaCheck"
                      label="I'm not a robot"
                      checked={captcha}
                      onChange={e => setCaptcha(e.target.checked)}
                      required
                      className="mb-0 fw-semibold"
                    />
                  </div>
                  <div className="text-center">
                    <span className="text-muted small d-block">reCAPTCHA</span>
                    <small className="text-muted">Security verification</small>
                  </div>
                </div>
              </div>

              {/* Submit Section */}
              <div className="text-center mt-5 pt-4 border-top">
                <div className="d-flex justify-content-center align-items-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="px-5 py-2 fw-bold shadow-sm"
                    style={{ borderRadius: '25px', minWidth: '200px' }}
                    disabled={
                      !captcha || 
                      !form.newPassword || 
                      !form.confirmPassword || 
                      form.newPassword !== form.confirmPassword ||
                      form.newPassword.length < 6 ||
                      loading
                    }
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Update Password
                      </>
                    )}
                  </Button>
                </div>
                <div className="mt-3">
                  <small className="text-muted">
                    <i className="fas fa-shield-alt me-1"></i>
                    Your password will be encrypted and stored securely
                  </small>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ChangePasswordTab; 